import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toggleAutomation, deleteAutomation } from '../../redux/Slices/automationSlice';
import { showSnackBar } from '../../redux/Slices/snackbarSlice';
import AutomationLogs from './AutomationLogs';

const IconBolt = ({ size = 14, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill={color}>
    <path d="M8.5 1.5L3 9h4.5l-1 5.5L13 7H8.5l1-5.5z" />
  </svg>
);

const IconPlay = ({ size = 14, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill={color}>
    <path d="M5 3.5v9l7-4.5-7-4.5z" />
  </svg>
);

const IconArrowDown = ({ size = 12, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.3">
    <path d="M8 4v8M5 9l3 3 3-3" />
  </svg>
);

const IconHistory = ({ size = 13, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1.5 8a6.5 6.5 0 1 1 1.1 3.6" />
    <path d="M1.5 12.5v-3h3" />
    <path d="M8 5v3.5l2.5 1.5" />
  </svg>
);

const IconChart = ({ size = 15, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12V9M8 12V5M12 12V7" />
  </svg>
);

const IconDots = ({ size = 15, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill={color}>
    <circle cx="8" cy="3.5" r="1.2" />
    <circle cx="8" cy="8" r="1.2" />
    <circle cx="8" cy="12.5" r="1.2" />
  </svg>
);

const IconEdit = ({ size = 14, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11.5 2.5l2 2L5 13H3v-2l8.5-8.5z" />
  </svg>
);

const IconTrash = ({ size = 14, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 4.5h10M6 4.5V3a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1.5M4.5 4.5L5 13.5a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1l.5-9" />
  </svg>
);

const TRIGGER_LABELS = {
  card_created: 'When a card is created',
  card_moved_to_list: 'When a card is moved to a list',
  card_moved_from_list: 'When a card is moved from a list',
  due_date_approaching: 'When due date is approaching',
  due_date_passed: 'When due date has passed',
  checklist_completed: 'When all checklist items completed',
  card_completed: 'When a card is marked complete',
  label_added: 'When a label is added',
  priority_changed: 'When priority is changed',
  schedule: 'On a schedule'
};

const ACTION_LABELS = {
  assign_member: 'Assign member',
  unassign_member: 'Unassign member',
  move_to_list: 'Move to list',
  set_due_date: 'Set due date',
  mark_complete: 'Mark complete',
  mark_incomplete: 'Mark incomplete',
  set_priority: 'Set priority',
  add_label: 'Add label',
  archive_card: 'Archive card',
  add_comment: 'Add comment',
  create_card: 'Create card',
  send_notification: 'Send notification'
};

const AutomationRuleItem = ({ automation, boardId, isAdmin }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showLogs, setShowLogs] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const handleToggle = async () => {
    try {
      await dispatch(toggleAutomation({ boardId, automationId: automation._id })).unwrap();
    } catch (error) {
      dispatch(showSnackBar({ message: error.message || 'Failed to toggle', type: 'error' }));
    }
  };

  const handleEdit = () => {
    setShowMenu(false);
    navigate(`/b/${boardId}/automations/${automation._id}/edit`);
  };

  const handleDelete = async () => {
    setShowMenu(false);
    if (!window.confirm('Delete this automation rule?')) return;
    try {
      await dispatch(deleteAutomation({ boardId, automationId: automation._id })).unwrap();
      dispatch(showSnackBar({ message: 'Automation deleted', type: 'success' }));
    } catch (error) {
      dispatch(showSnackBar({ message: error.message || 'Failed to delete', type: 'error' }));
    }
  };

  const formatRawType = (type) => type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Unknown';
  const triggerLabel = TRIGGER_LABELS[automation.trigger?.type] || formatRawType(automation.trigger?.type);
  const actionLabels = automation.actions?.map(a => ACTION_LABELS[a.type] || formatRawType(a.type)).join(', ');

  return (
    <div className={`automation-rule-card ${!automation.enabled ? 'disabled' : ''}`}>
      <div className="rule-card-header">
        <h3 className="rule-name" title={automation.name}>{automation.name}</h3>
        {isAdmin && (
          <div className="rule-toggle">
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={automation.enabled}
                onChange={handleToggle}
              />
              <span className="toggle-slider" />
            </label>
          </div>
        )}
      </div>

      <div className="rule-card-body">
        <div className="rule-flow">
          <div className="flow-step trigger-step">
            <span className="flow-icon"><IconBolt /></span>
            <span className="flow-text">{triggerLabel}</span>
          </div>
          <div className="flow-divider">
            <IconArrowDown color="var(--primary-text-color)" />
          </div>
          <div className="flow-step action-step">
            <span className="flow-icon"><IconPlay /></span>
            <span className="flow-text">{actionLabels}</span>
          </div>
        </div>
      </div>

      <div className="rule-card-footer">
        <div className="rule-stats">
          {automation.runCount > 0 ? (
            <>
              <IconHistory />
              Ran {automation.runCount} time{automation.runCount !== 1 ? 's' : ''}
              {automation.lastTriggeredAt && ` \u2022 ${new Date(automation.lastTriggeredAt).toLocaleDateString()}`}
            </>
          ) : (
            <span className="rule-new-badge">New</span>
          )}
        </div>

        <div className="rule-actions">
          <button
            className="rule-action-btn"
            onClick={() => setShowLogs(true)}
            title="View logs"
          >
            <IconChart />
          </button>
          {isAdmin && (
            <div className="rule-menu-wrapper">
              <button
                className="rule-action-btn"
                onClick={() => setShowMenu(!showMenu)}
                title="More options"
              >
                <IconDots />
              </button>
              {showMenu && (
                <div className="rule-menu" onMouseLeave={() => setShowMenu(false)}>
                  <button onClick={handleEdit}>
                    <IconEdit />
                    Edit
                  </button>
                  <button onClick={handleDelete} className="delete-option">
                    <IconTrash />
                    Delete
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showLogs && (
        <AutomationLogs
          boardId={boardId}
          automationId={automation._id}
          automationName={automation.name}
          onClose={() => setShowLogs(false)}
        />
      )}
    </div>
  );
};

export default AutomationRuleItem;
